import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import GradingService, {
  SubmissionForGrading,
  SubmissionStats,
  StudentWithSubmissions,
  StudentSubmission,
  StepResource,
} from "../../_services/GradingService";
import RubricScoringService, {
  SingleSubmissionEvaluationResult,
} from "../../_services/RubricScoringService";
import StudyResourceSubmissionService from "../../_services/StudyResourceSubmissionService";
import "./ai-grading-studio.css";

interface SubmissionDetails {
  Id: number;
  UserId: number;
  StudyResourceId: number;
  SubmissionType: string;
  SubmissionContextData: any;
  SubmissionData: any;
  SubmissionText: string;
  AttachmentURL: string | null;
  Score: string | null;
  MaxScore: string | null;
  Grade: string | null;
  Status: string;
  SubmittedAt: string;
  FeedbackText: string | null;
  ReviewerComments: string | null;
}

interface RubricCriteria {
  Id: number;
  Name: string;
  Code: string;
  MaxScore: number;
  Criterion: {
    levels: Array<{
      level: string;
      score: number;
      description: string;
    }>;
  };
}

const AIGradingStudio: React.FC = () => {
  // URL params
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const urlStepId = urlParams.get("stepId");
  const urlBatchId = urlParams.get("batchId");

  // Step resources state (when accessed via stepId)
  const [stepResources, setStepResources] = useState<StepResource[]>([]);
  const [stepResourcesLoading, setStepResourcesLoading] = useState(false);
  const [selectedResourceId, setSelectedResourceId] = useState<number | null>(null);

  // View mode state
  const [gradingViewMode, setGradingViewMode] = useState<"assignment" | "student">("assignment");

  // State management
  const [submissions, setSubmissions] = useState<SubmissionForGrading[]>([]);
  const [stats, setStats] = useState<SubmissionStats | null>(null);
  const [selectedSubmission, setSelectedSubmission] = useState<SubmissionForGrading | null>(null);
  const [submissionDetails, setSubmissionDetails] = useState<SubmissionDetails | null>(null);
  const [rubricCriteria, setRubricCriteria] = useState<RubricCriteria | null>(null);
  const [aiEvaluation, setAiEvaluation] = useState<SingleSubmissionEvaluationResult | null>(null);

  // Filter state
  const [statusFilter, setStatusFilter] = useState<string[]>(["SUBMITTED"]);
  const [sortBy, setSortBy] = useState<"dueDate" | "submittedAt" | "studentName">("submittedAt");

  // Grading state
  const [currentScore, setCurrentScore] = useState<number>(0);
  const [currentGrade, setCurrentGrade] = useState<string>("");
  const [feedbackText, setFeedbackText] = useState<string>("");
  const [reviewerComments, setReviewerComments] = useState<string>("");

  // UI state
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'formatted' | 'json'>('formatted');
  const [activeTab, setActiveTab] = useState<'response' | 'context' | 'rubric' | 'ai'>('response');

  // Feedback edit state
  const [feedbackEditMode, setFeedbackEditMode] = useState(false);

  // Modal state
  const [showResetModal, setShowResetModal] = useState(false);
  const [showClearModal, setShowClearModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalSubmissionId, setModalSubmissionId] = useState<number | null>(null);
  const [clearOptions, setClearOptions] = useState({
    clearSubmissionData: true,
    clearSubmissionContextData: false,
    resetStatus: false
  });
  const [resetSessionOnDelete, setResetSessionOnDelete] = useState(true);
  const [showResetSessionModal, setShowResetSessionModal] = useState(false);
  const [resetSessionSubmission, setResetSessionSubmission] = useState<SubmissionForGrading | null>(null);

  // Student mode state (for "By Student" view)
  const [students, setStudents] = useState<StudentWithSubmissions[]>([]);
  const [allStudents, setAllStudents] = useState<StudentWithSubmissions[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<StudentWithSubmissions | null>(null);
  const [studentSearchTerm, setStudentSearchTerm] = useState<string>("");
  const [studentStatusFilter, setStudentStatusFilter] = useState<string>("ALL");
  const [studentSortBy, setStudentSortBy] = useState<"name" | "submissionCount" | "pendingCount">("name");
  const [studentsLoading, setStudentsLoading] = useState(false);

  // Load step resources if stepId is provided in URL
  useEffect(() => {
    const loadStepResources = async () => {
      if (!urlStepId) return;

      setStepResourcesLoading(true);
      try {
        const response = await GradingService.getResourcesByStep(Number(urlStepId));
        if (response.success && response.data) {
          setStepResources(response.data);
          // Auto-select first resource if available
          if (response.data.length > 0) {
            setSelectedResourceId(response.data[0].id);
          }
        }
      } catch (err) {
        console.error("Error loading step resources:", err);
      } finally {
        setStepResourcesLoading(false);
      }
    };

    loadStepResources();
  }, [urlStepId]);

  // Load submissions on mount or when filters/resource changes
  useEffect(() => {
    loadSubmissions();
  }, [statusFilter, sortBy, selectedResourceId]);

  // Keyboard shortcuts: J (prev), K (next)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if typing in an input/textarea
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) return;

      if (e.key === 'j' || e.key === 'J') {
        e.preventDefault();
        goToPrev();
      } else if (e.key === 'k' || e.key === 'K') {
        e.preventDefault();
        goToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [submissions, selectedSubmission]);

  // Load students when switching to student mode
  useEffect(() => {
    if (gradingViewMode === "student" && allStudents.length === 0) {
      loadStudents();
    }
  }, [gradingViewMode]);

  // Client-side filtering and sorting for students
  useEffect(() => {
    let filtered = [...allStudents];

    // Filter by search term
    if (studentSearchTerm) {
      const searchLower = studentSearchTerm.toLowerCase();
      filtered = filtered.filter(student =>
        (student.name?.toLowerCase().includes(searchLower)) ||
        (student.studentCode?.toLowerCase().includes(searchLower)) ||
        (student.userCode?.toLowerCase().includes(searchLower))
      );
    }

    // Sort
    switch (studentSortBy) {
      case "submissionCount":
        filtered.sort((a, b) => b.totalSubmissions - a.totalSubmissions);
        break;
      case "pendingCount":
        filtered.sort((a, b) => b.pendingCount - a.pendingCount);
        break;
      case "name":
      default:
        filtered.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        break;
    }

    setStudents(filtered);
  }, [allStudents, studentSearchTerm, studentSortBy]);

  // Helper functions
  const getInitials = (name: string | undefined): string => {
    if (!name) return '??';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toLowerCase();
    }
    return name.slice(0, 2).toLowerCase();
  };

  const getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case 'SUBMITTED': return 'good';
      case 'IN_REVIEW': return 'warn';
      case 'GRADED': return 'good';
      default: return '';
    }
  };

  const toggleStatusFilter = (status: string) => {
    setStatusFilter(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const getSelectedIndex = (): number => {
    if (!selectedSubmission) return -1;
    return submissions.findIndex(s => s.id === selectedSubmission.id);
  };

  const goToPrev = useCallback(() => {
    if (submissions.length === 0) return;
    const idx = getSelectedIndex();
    if (idx > 0) {
      handleSelectSubmission(submissions[idx - 1]);
    } else if (idx === -1 && submissions.length > 0) {
      handleSelectSubmission(submissions[0]);
    }
  }, [submissions, selectedSubmission]);

  const goToNext = useCallback(() => {
    if (submissions.length === 0) return;
    const idx = getSelectedIndex();
    if (idx < submissions.length - 1) {
      handleSelectSubmission(submissions[idx + 1]);
    } else if (idx === -1 && submissions.length > 0) {
      handleSelectSubmission(submissions[0]);
    }
  }, [submissions, selectedSubmission]);

  /**
   * Load submissions from API
   */
  const loadSubmissions = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await GradingService.getSubmissions({
        studyResourceId: selectedResourceId || undefined,
        status: statusFilter,
        sort: sortBy,
        limit: 50,
        offset: 0,
      });

      if (response.success && response.data) {
        setSubmissions(response.data.submissions);
        setStats(response.data.stats);
      } else {
        setError(response.error || "Failed to load submissions");
      }
    } catch (err) {
      setError("Error loading submissions");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load students for student-centric view
   */
  const loadStudents = async () => {
    setStudentsLoading(true);
    setError(null);

    try {
      const response = await GradingService.getStudentsWithSubmissions({
        studyResourceId: selectedResourceId || undefined,
        batchId: urlBatchId ? Number(urlBatchId) : undefined,
        status: ["SUBMITTED", "IN_REVIEW", "GRADED"],
        sort: "name",
        limit: 100,
      });

      if (response.success && response.data) {
        setAllStudents(response.data.students);
        if (response.data.students.length === 0) {
          setError("No students found with submissions.");
        }
      } else {
        setError(response.error || "Failed to load students");
      }
    } catch (err) {
      setError(`Error loading students: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setStudentsLoading(false);
    }
  };

  /**
   * Select a student (clears submission selection)
   */
  const handleSelectStudent = (student: StudentWithSubmissions) => {
    setSelectedStudent(student);
    setSelectedSubmission(null);
    setSubmissionDetails(null);
    setAiEvaluation(null);
  };

  /**
   * Handle submission selection from student view
   * StudentSubmission extends SubmissionForGrading, so we can use it directly
   */
  const handleSelectStudentSubmission = async (submission: StudentSubmission) => {
    await handleSelectSubmission(submission);
  };

  /**
   * Get filtered submissions for selected student
   */
  const getFilteredStudentSubmissions = (submissions: StudentSubmission[]) => {
    if (studentStatusFilter === "ALL") return submissions;
    return submissions.filter(s => s.status === studentStatusFilter);
  };

  /**
   * Select a submission and load its details
   */
  const handleSelectSubmission = async (submission: SubmissionForGrading) => {
    setSelectedSubmission(submission);
    setAiEvaluation(null);
    setRubricCriteria(null);
    setError(null);
    setActiveTab('response');

    // Clear grading form fields
    setCurrentScore(0);
    setCurrentGrade("");
    setFeedbackText("");
    setReviewerComments("");

    try {
      const response = await StudyResourceSubmissionService.getSubmissionById(submission.id);
      if (response.submission) {
        setSubmissionDetails(response.submission);

        if (response.rubric) {
          setRubricCriteria(response.rubric);
        }

        if (response.submission.Score) {
          setCurrentScore(parseFloat(response.submission.Score));
        }
        if (response.submission.Grade) {
          setCurrentGrade(response.submission.Grade);
        }
        if (response.submission.FeedbackText) {
          setFeedbackText(response.submission.FeedbackText);
        }
        if (response.submission.ReviewerComments) {
          setReviewerComments(response.submission.ReviewerComments);
        }
      }
    } catch (err) {
      setError("Failed to load submission details");
      console.error(err);
    }
  };

  /**
   * Get AI evaluation for current submission
   */
  const handleGetAIEvaluation = async () => {
    if (!selectedSubmission) return;

    setAiLoading(true);
    setError(null);

    try {
      const response = await RubricScoringService.evaluateSingleSubmission(
        selectedSubmission.id
      );

      if (response.success && response.data) {
        setAiEvaluation(response.data);
        setCurrentScore(response.data.score);
        setCurrentGrade(response.data.grade);
        setFeedbackText(response.data.feedback);
      } else {
        setError(response.error || "Failed to get AI evaluation");
      }
    } catch (err) {
      setError("Error getting AI evaluation");
      console.error(err);
    } finally {
      setAiLoading(false);
    }
  };

  /**
   * Open reset grade modal
   */
  const handleResetGradeClick = (submissionId: number, event?: React.MouseEvent) => {
    if (event) event.stopPropagation();
    setModalSubmissionId(submissionId);
    setShowResetModal(true);
  };

  /**
   * Confirm and execute reset grade
   */
  const confirmResetGrade = async () => {
    if (!modalSubmissionId) return;

    setShowResetModal(false);
    setLoading(true);
    setError(null);

    try {
      const response = await StudyResourceSubmissionService.resetGrade(modalSubmissionId);

      if (response.success) {
        await loadSubmissions();

        if (selectedSubmission?.id === modalSubmissionId) {
          await handleSelectSubmission(selectedSubmission);
        }

        setError(null);
      } else {
        setError(response.message || "Failed to reset grade");
      }
    } catch (err) {
      setError("Error resetting grade");
      console.error(err);
    } finally {
      setLoading(false);
      setModalSubmissionId(null);
    }
  };

  /**
   * Open clear data modal
   */
  const handleClearDataClick = (submissionId: number, event?: React.MouseEvent) => {
    if (event) event.stopPropagation();
    setModalSubmissionId(submissionId);
    setClearOptions({
      clearSubmissionData: true,
      clearSubmissionContextData: false,
      resetStatus: false
    });
    setShowClearModal(true);
  };

  /**
   * Confirm and execute clear data
   */
  const confirmClearData = async () => {
    if (!modalSubmissionId) return;

    if (!clearOptions.clearSubmissionData && !clearOptions.clearSubmissionContextData && !clearOptions.resetStatus) {
      setError("Please select at least one option to clear");
      return;
    }

    setShowClearModal(false);
    setLoading(true);
    setError(null);

    try {
      const response = await StudyResourceSubmissionService.clearSubmissionData(modalSubmissionId, clearOptions);

      if (response.success) {
        await loadSubmissions();

        if (selectedSubmission?.id === modalSubmissionId) {
          await handleSelectSubmission(selectedSubmission);
        }

        setError(null);
      } else {
        setError(response.message || "Failed to clear data");
      }
    } catch (err) {
      setError("Error clearing data");
      console.error(err);
    } finally {
      setLoading(false);
      setModalSubmissionId(null);
    }
  };

  /**
   * Open delete submission modal
   */
  const handleDeleteClick = (submissionId: number, event?: React.MouseEvent) => {
    if (event) event.stopPropagation();
    setModalSubmissionId(submissionId);
    setShowDeleteModal(true);
  };

  /**
   * Confirm and execute delete submission
   */
  const confirmDeleteSubmission = async () => {
    if (!modalSubmissionId) return;

    setShowDeleteModal(false);
    setLoading(true);
    setError(null);

    try {
      // Find the submission to get resourceId and userId for session reset
      const submissionToDelete = submissions.find(s => s.id === modalSubmissionId);

      const response = await StudyResourceSubmissionService.deleteSubmission(modalSubmissionId);

      if (response.success) {
        // Also reset workspace session if checkbox was checked
        if (resetSessionOnDelete && submissionToDelete) {
          await StudyResourceSubmissionService.resetWorkspaceSession(
            submissionToDelete.studyResourceId,
            submissionToDelete.userId
          );
        }

        if (selectedSubmission?.id === modalSubmissionId) {
          setSelectedSubmission(null);
          setSubmissionDetails(null);
        }

        await loadSubmissions();
        setError(null);
      } else {
        setError(response.message || "Failed to delete submission");
      }
    } catch (err) {
      setError("Error deleting submission");
      console.error(err);
    } finally {
      setLoading(false);
      setModalSubmissionId(null);
    }
  };

  /**
   * Open reset session modal
   */
  const handleResetSessionClick = (submission: SubmissionForGrading, event?: React.MouseEvent) => {
    if (event) event.stopPropagation();
    setResetSessionSubmission(submission);
    setShowResetSessionModal(true);
  };

  /**
   * Confirm and execute reset session
   */
  const confirmResetSession = async () => {
    if (!resetSessionSubmission) return;

    setShowResetSessionModal(false);
    setLoading(true);
    setError(null);

    try {
      const response = await StudyResourceSubmissionService.resetWorkspaceSession(
        resetSessionSubmission.studyResourceId,
        resetSessionSubmission.userId
      );

      if (response.success) {
        setError(null);
      } else {
        setError(response.message || "Failed to reset session");
      }
    } catch (err) {
      setError("Error resetting session");
      console.error(err);
    } finally {
      setLoading(false);
      setResetSessionSubmission(null);
    }
  };

  /**
   * Save grade and move to next submission
   */
  const handleSaveAndNext = async () => {
    if (!selectedSubmission || !submissionDetails) return;

    setSaving(true);
    setError(null);

    try {
      const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
      const reviewerId = currentUser.userId || 0;

      const response = await GradingService.updateGrade(selectedSubmission.id, {
        score: currentScore,
        maxScore: submissionDetails.MaxScore ? parseFloat(submissionDetails.MaxScore) : 100,
        grade: currentGrade,
        feedbackText: feedbackText,
        reviewerComments: reviewerComments,
        status: "GRADED",
        reviewedByUserId: reviewerId,
      });

      if (response.success) {
        const currentIndex = submissions.findIndex(s => s.id === selectedSubmission.id);
        if (currentIndex < submissions.length - 1) {
          const nextSubmission = submissions[currentIndex + 1];
          handleSelectSubmission(nextSubmission);
        } else {
          loadSubmissions();
          setSelectedSubmission(null);
          setSubmissionDetails(null);
        }

        setCurrentScore(0);
        setCurrentGrade("");
        setFeedbackText("");
        setReviewerComments("");
        setAiEvaluation(null);
      } else {
        setError(response.error || "Failed to save grade");
      }
    } catch (err) {
      setError("Error saving grade");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Render formatted business view of submission data
   */
  const renderFormattedData = (data: any, skipKeys: string[] = []) => {
    if (!data) return null;

    // Keys to exclude from the main formatted view (metadata shown in Submission Info)
    const defaultSkipKeys = ['turnsUsed', 'totalTurns', 'totalTokensUsed'];
    const keysToSkip = [...defaultSkipKeys, ...skipKeys];

    const renderObject = (obj: any, depth = 0): JSX.Element[] => {
      return Object.entries(obj)
        .filter(([key]) => !keysToSkip.includes(key))
        .map(([key, value]) => {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          return (
            <div key={key} className={`data-group depth-${depth}`}>
              <div className="data-label">{formatLabel(key)}</div>
              <div className="data-nested">
                {renderObject(value, depth + 1)}
              </div>
            </div>
          );
        } else if (Array.isArray(value)) {
          return (
            <div key={key} className={`data-group depth-${depth}`}>
              <div className="data-label">{formatLabel(key)}</div>
              <div className="data-array">
                {value.map((item, idx) => (
                  <div key={idx} className="array-item">
                    {typeof item === 'object' ? renderObject(item, depth + 1) : String(item)}
                  </div>
                ))}
              </div>
            </div>
          );
        } else {
          return (
            <div key={key} className={`data-row depth-${depth}`}>
              <span className="data-key">{formatLabel(key)}:</span>
              <span className="data-value">{String(value)}</span>
            </div>
          );
        }
      });
    };

    const formatLabel = (str: string) => {
      return str
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (s) => s.toUpperCase())
        .trim();
    };

    return <div className="formatted-data">{renderObject(data)}</div>;
  };

  /**
   * Render the main viewer tab content
   */
  const renderTabContent = () => {
    if (!submissionDetails) return null;

    switch (activeTab) {
      case 'response':
        return (
          <>
            {submissionDetails.SubmissionData && (
              viewMode === 'json' ? (
                <pre className="content-box json-view">
                  {JSON.stringify(submissionDetails.SubmissionData, null, 2)}
                </pre>
              ) : (
                <div className="content-box formatted-view">
                  {renderFormattedData(submissionDetails.SubmissionData)}
                </div>
              )
            )}
            {submissionDetails.SubmissionText &&
             submissionDetails.SubmissionText !== submissionDetails.SubmissionData?.reflection && (
              <div className="content-box" style={{ marginTop: 12 }}>
                {submissionDetails.SubmissionText}
              </div>
            )}
            {!submissionDetails.SubmissionData && !submissionDetails.SubmissionText && (
              <div style={{ color: 'var(--muted2)', padding: 20, textAlign: 'center' }}>
                No submission data available.
              </div>
            )}
          </>
        );

      case 'context':
        return submissionDetails.SubmissionContextData ? (
          viewMode === 'json' ? (
            <pre className="content-box json-view">
              {JSON.stringify(submissionDetails.SubmissionContextData, null, 2)}
            </pre>
          ) : (
            <div className="content-box formatted-view">
              {renderFormattedData(submissionDetails.SubmissionContextData)}
            </div>
          )
        ) : (
          <div style={{ color: 'var(--muted2)', padding: 20, textAlign: 'center' }}>
            No context data available.
          </div>
        );

      case 'rubric':
        return rubricCriteria ? (
          <div className="rubric-panel">
            <h3>Grading Rubric: {rubricCriteria.Name}</h3>
            <div className="rubric-info">
              <span className="rubric-code">{rubricCriteria.Code}</span>
              <span className="rubric-maxscore">Max Score: {rubricCriteria.MaxScore}</span>
            </div>
            <div className="rubric-levels">
              {rubricCriteria.Criterion.levels.map((level, idx) => (
                <div key={idx} className="rubric-level">
                  <div className="level-header">
                    <span className="level-name">{level.level}</span>
                    <span className="level-score">{level.score} points</span>
                  </div>
                  <p className="level-description">{level.description}</p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ color: 'var(--muted2)', padding: 20, textAlign: 'center' }}>
            No rubric available for this submission.
          </div>
        );

      case 'ai':
        return aiEvaluation ? (
          <div className="ai-results-compact">
            <div className="ai-score-compact">
              <strong>AI Score:</strong> {aiEvaluation.score}/{aiEvaluation.maxScore} ({aiEvaluation.grade})
            </div>
            <div className="ai-feedback-compact">
              <strong>AI Feedback:</strong>
              <div className="markdown-content"><ReactMarkdown>{aiEvaluation.feedback}</ReactMarkdown></div>
            </div>
            {aiEvaluation.criteriaScores && aiEvaluation.criteriaScores.length > 0 && (
              <div className="ai-criteria-compact">
                <strong>Criteria Breakdown:</strong>
                {aiEvaluation.criteriaScores.map((criterion, idx) => (
                  <div key={idx} className="criterion-item-compact">
                    <span className="criterion-name">{criterion.criterionName}:</span>
                    <span className="criterion-score">
                      {criterion.score}/{criterion.maxScore}
                    </span>
                    <div className="criterion-feedback"><ReactMarkdown>{criterion.feedback}</ReactMarkdown></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div style={{ color: 'var(--muted2)', padding: 20, textAlign: 'center' }}>
            Click "AI Evaluate" in the top bar to generate an AI evaluation.
          </div>
        );

      default:
        return null;
    }
  };

  const selectedIndex = getSelectedIndex();
  const maxScore = submissionDetails?.MaxScore ? parseFloat(submissionDetails.MaxScore) : 100;

  // Helper to format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  // Helper to get status class for student submissions
  const getStudentStatusClass = (status: string) => {
    switch (status) {
      case "SUBMITTED": return "status-pending";
      case "IN_REVIEW": return "status-review";
      case "GRADED":
      case "RETURNED":
      case "COMPLETED": return "status-graded";
      default: return "";
    }
  };

  return (
    <div className="ai-grading-studio">
      {/* Drawer collapse toggle (pure CSS) */}
      <input id="drawerToggle" type="checkbox" style={{ display: 'none' }} />

      {/* TOPBAR */}
      <header className="topbar">
        <div className="left">
          <div className="brand">
            <div className="logo" aria-hidden="true" />
            <div>
              <strong>AI Grading Studio</strong>
              <div className="crumbs">{gradingViewMode === 'student' ? 'Student View' : 'Assignment Grading'}</div>
            </div>
          </div>
          <div className="view-toggle-switch">
            <button
              className={`toggle-btn ${gradingViewMode === 'assignment' ? 'active' : ''}`}
              onClick={() => setGradingViewMode("assignment")}
            >
              By Assignment
            </button>
            <button
              className={`toggle-btn ${gradingViewMode === 'student' ? 'active' : ''}`}
              onClick={() => setGradingViewMode("student")}
            >
              By Student
            </button>
          </div>
        </div>

        <div className="center">
          <button className="navbtn" type="button" title="Previous (J)" onClick={goToPrev}>
            &#9664; <span className="kbd">J</span>
          </button>
          <div className="pill" title="Status">
            <span className="dot" aria-hidden="true" />
            <span>{selectedSubmission?.status || 'Select'}</span>
            <span style={{ color: 'var(--muted2)' }}>&bull;</span>
            <span style={{ color: 'var(--muted)' }}>
              {selectedIndex >= 0 ? `${selectedIndex + 1} of ${submissions.length}` : `${submissions.length} total`}
            </span>
          </div>
          <button className="navbtn" type="button" title="Next (K)" onClick={goToNext}>
            <span className="kbd">K</span> &#9654;
          </button>
        </div>

        <div className="right">
          {stats && (
            <div className="pill" title="Quick stats">
              Total: {stats.total} &bull; Pending: {stats.pending} &bull; Graded: {stats.graded}
            </div>
          )}
          <button
            className="navbtn ghost"
            type="button"
            onClick={handleSaveAndNext}
            disabled={saving || !selectedSubmission}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button
            className="navbtn primary"
            type="button"
            onClick={handleGetAIEvaluation}
            disabled={aiLoading || !selectedSubmission || !rubricCriteria}
            title={selectedSubmission && !rubricCriteria ? 'No rubric assigned to this resource' : ''}
          >
            {aiLoading ? 'Evaluating...' : 'AI Evaluate'}
          </button>
          {selectedSubmission && !rubricCriteria && !aiLoading && (
            <span style={{ color: '#e57373', fontSize: 11, marginLeft: 8, whiteSpace: 'nowrap' }}>No rubric set</span>
          )}
        </div>
      </header>

      {/* LAYOUT */}
      <div className="layout">
        {/* LEFT RAIL - Conditional based on viewMode */}
        <aside className="rail">
          {gradingViewMode === 'assignment' ? (
            /* ASSIGNMENT MODE: Show submissions list */
            <>
              <div className="railHead">
                <h3>Submissions</h3>
                <span className="badge">{submissions.length}</span>
              </div>
              <div className="railBody">
                {/* Search */}
                <div className="search">
                  <input className="input" placeholder="Search student, id..." />
                  <button className="navbtn" type="button" title="Refresh" onClick={loadSubmissions}>
                    {loading ? '...' : '☰'}
                  </button>
                </div>

                {/* Filter chips */}
                <div className="chipRow" aria-label="Filters">
                  {["SUBMITTED", "IN_REVIEW", "GRADED"].map(status => (
                    <div
                      key={status}
                      className={`chip ${statusFilter.includes(status) ? 'active' : ''}`}
                      onClick={() => toggleStatusFilter(status)}
                    >
                      {status.replace('_', ' ')}
                    </div>
                  ))}
                </div>

                {/* Sort dropdown */}
                <select
                  className="input"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                >
                  <option value="submittedAt">Sort: Submission Time</option>
                  <option value="dueDate">Sort: Due Date</option>
                  <option value="studentName">Sort: Student Name</option>
                </select>

                {/* Resource selector (when accessed via stepId) */}
                {stepResources.length > 0 && (
                  <select
                    className="input"
                    value={selectedResourceId || ""}
                    onChange={(e) => setSelectedResourceId(e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value="">All Resources</option>
                    {stepResources.map((resource) => (
                      <option key={resource.id} value={resource.id}>
                        {resource.name || `Resource ${resource.id}`}
                      </option>
                    ))}
                  </select>
                )}

                {error && <div className="error-message">{error}</div>}

                {/* Submission list */}
                <div className="list">
                  {submissions.map((submission) => {
                    const isActive = selectedSubmission?.id === submission.id;
                    const initials = getInitials(submission.userName);
                    const statusClass = getStatusBadgeClass(submission.status);
                    const dateStr = new Date(submission.submittedAt).toLocaleString();

                    return (
                      <div
                        key={submission.id}
                        className={`item ${isActive ? 'active' : ''}`}
                        role="button"
                        aria-label={`Open submission ${submission.id}`}
                        onClick={() => handleSelectSubmission(submission)}
                      >
                        <div>
                          <div className="itemTop">
                            <div className="avatar">{initials}</div>
                            <div>
                              <div className="name">
                                {submission.userName || `User ${submission.userId}`}
                              </div>
                              <div className="sub">
                                {submission.resourceName || `Resource ${submission.studyResourceId}`} &bull; {dateStr}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end' }}>
                          <span className={`badge ${statusClass}`}>{submission.status}</span>
                          <span className="badge">#{submission.id}</span>
                        </div>

                        {submission.score && (
                          <div className="submission-score">
                            Score: {submission.score}/{submission.maxScore} ({submission.grade})
                          </div>
                        )}

                        {/* Hover-only actions */}
                        <div className="submission-actions">
                          <button
                            className="btn-reset-grade"
                            onClick={(e) => handleResetGradeClick(submission.id, e)}
                            title="Reset grade"
                          >
                            Reset Grade
                          </button>
                          <button
                            className="btn-clear-data"
                            onClick={(e) => handleClearDataClick(submission.id, e)}
                            title="Clear data"
                          >
                            Clear Data
                          </button>
                          <button
                            className="btn-delete"
                            onClick={(e) => handleDeleteClick(submission.id, e)}
                            title="Delete"
                          >
                            Delete
                          </button>
                          <button
                            className="btn-reset-session"
                            onClick={(e) => handleResetSessionClick(submission, e)}
                            title="Reset workspace session"
                          >
                            Reset Session
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  {submissions.length === 0 && !loading && (
                    <div style={{ color: 'var(--muted2)', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>
                      No submissions found.
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            /* STUDENT MODE: Show students list + selected student's submissions */
            <>
              <div className="railHead">
                <h3>Students</h3>
                <span className="badge">{students.length}</span>
                <button className="navbtn" type="button" title="Refresh" onClick={loadStudents} style={{ marginLeft: 'auto' }}>
                  {studentsLoading ? '...' : '↻'}
                </button>
              </div>
              <div className="railBody">
                {/* Search */}
                <div className="search">
                  <input
                    className="input"
                    placeholder="Search students..."
                    value={studentSearchTerm}
                    onChange={(e) => setStudentSearchTerm(e.target.value)}
                  />
                </div>

                {/* Filter dropdowns */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  <select
                    className="input"
                    value={studentStatusFilter}
                    onChange={(e) => setStudentStatusFilter(e.target.value)}
                    style={{ flex: 1 }}
                  >
                    <option value="ALL">All Status</option>
                    <option value="SUBMITTED">Submitted</option>
                    <option value="IN_REVIEW">In Review</option>
                    <option value="GRADED">Graded</option>
                  </select>
                  <select
                    className="input"
                    value={studentSortBy}
                    onChange={(e) => setStudentSortBy(e.target.value as any)}
                    style={{ flex: 1 }}
                  >
                    <option value="name">By Name</option>
                    <option value="pendingCount">By Pending</option>
                    <option value="submissionCount">By Total</option>
                  </select>
                </div>

                {error && <div className="error-message">{error}</div>}

                {/* Student list */}
                <div className="list" style={{ maxHeight: selectedStudent ? '35%' : '100%' }}>
                  {studentsLoading ? (
                    <div style={{ color: 'var(--muted2)', padding: 20, textAlign: 'center' }}>Loading...</div>
                  ) : students.length > 0 ? (
                    students.map((student) => (
                      <div
                        key={student.id}
                        className={`item ${selectedStudent?.id === student.id ? 'active' : ''}`}
                        role="button"
                        onClick={() => handleSelectStudent(student)}
                      >
                        <div>
                          <div className="itemTop">
                            <div className="avatar">{getInitials(student.name)}</div>
                            <div>
                              <div className="name">{student.name || "Unknown"}</div>
                              <div className="sub">
                                {student.pendingCount}P &bull; {student.totalSubmissions}T
                                {student.averageScore !== null && ` &bull; ${student.averageScore}%`}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ color: 'var(--muted2)', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>
                      No students found.
                    </div>
                  )}
                </div>

                {/* Selected student's submissions */}
                {selectedStudent && (
                  <div className="student-submissions-section">
                    <div className="railHead" style={{ marginTop: 16 }}>
                      <h4>{selectedStudent.name}'s Submissions</h4>
                      <span className="badge">{getFilteredStudentSubmissions(selectedStudent.submissions).length}</span>
                    </div>
                    <div className="list">
                      {getFilteredStudentSubmissions(selectedStudent.submissions).map((submission) => (
                        <div
                          key={submission.id}
                          className={`item ${selectedSubmission?.id === submission.id ? 'active' : ''}`}
                          role="button"
                          onClick={() => handleSelectStudentSubmission(submission)}
                        >
                          <div>
                            <div className="name" style={{ fontSize: 13 }}>
                              {submission.resourceName || `#${submission.id}`}
                            </div>
                            <div className="sub">
                              <span className={`status-dot ${getStudentStatusClass(submission.status)}`} style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', marginRight: 6 }}></span>
                              {formatDate(submission.submittedAt)}
                              {submission.score && ` • ${submission.score}/${submission.maxScore}`}
                            </div>
                          </div>
                          <span className={`badge ${getStatusBadgeClass(submission.status)}`}>{submission.status}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </aside>

        {/* MAIN VIEWER */}
        <main className="main">
          {selectedSubmission && submissionDetails ? (
            <>
              <div className="mainHead">
                <div className="titleBlock">
                  <div className="titleRow">
                    <h2>Submission #{selectedSubmission.id} &bull; {selectedSubmission.userName || `User ${selectedSubmission.userId}`}</h2>
                    <span className={`badge ${getStatusBadgeClass(selectedSubmission.status)}`}>
                      {selectedSubmission.status}
                    </span>
                    {selectedSubmission.isLate && <span className="badge warn">LATE</span>}
                  </div>
                  <div className="metaRow">
                    <span>Submitted: {new Date(selectedSubmission.submittedAt).toLocaleString()}</span>
                    <span style={{ opacity: .45 }}>&bull;</span>
                    <span>Resource: {selectedSubmission.resourceName || `ID ${selectedSubmission.studyResourceId}`}</span>
                  </div>
                </div>

                <div className="tabs" aria-label="Viewer tabs">
                  <div
                    className={`tab ${activeTab === 'response' ? 'active' : ''}`}
                    onClick={() => setActiveTab('response')}
                  >
                    Student Response
                  </div>
                  {submissionDetails.SubmissionContextData && (
                    <div
                      className={`tab ${activeTab === 'context' ? 'active' : ''}`}
                      onClick={() => setActiveTab('context')}
                    >
                      Context
                    </div>
                  )}
                  <div
                    className={`tab ${activeTab === 'rubric' ? 'active' : ''}`}
                    onClick={() => setActiveTab('rubric')}
                  >
                    Rubric
                  </div>
                  {aiEvaluation && (
                    <div
                      className={`tab ${activeTab === 'ai' ? 'active' : ''}`}
                      onClick={() => setActiveTab('ai')}
                    >
                      AI Evaluation
                    </div>
                  )}
                </div>
              </div>

              <div className="viewerWrap">
                {/* Main viewer box */}
                <section className="viewer" aria-label="Submission viewer">
                  <div className="viewerToolbar">
                    <div className="toolGroup">
                      <div
                        className={`tool ${viewMode === 'formatted' ? 'active' : ''}`}
                        onClick={() => setViewMode('formatted')}
                      >
                        Formatted
                      </div>
                      <div
                        className={`tool ${viewMode === 'json' ? 'active' : ''}`}
                        onClick={() => setViewMode('json')}
                      >
                        JSON
                      </div>
                    </div>
                    <div className="toolGroup">
                      {submissionDetails.AttachmentURL && (
                        <a
                          href={submissionDetails.AttachmentURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="tool"
                          style={{ textDecoration: 'none' }}
                        >
                          View Attachment
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="viewerBody">
                    {renderTabContent()}
                  </div>
                </section>

                {/* Collapsible details: Submission Info */}
                {(submissionDetails.SubmissionContextData || submissionDetails.SubmissionData) && (
                  <details>
                    <summary>
                      <div className="sumLeft">
                        <div className="caret" aria-hidden="true" />
                        <div>
                          <div className="sumTitle">Submission Info</div>
                          <div className="sumMeta">Context, stats, and metadata</div>
                        </div>
                      </div>
                      <div className="kpiRow" aria-hidden="true">
                        {/* Stats from SubmissionData */}
                        {submissionDetails.SubmissionData?.turnsUsed && (
                          <div className="kpi">Turns: {submissionDetails.SubmissionData.turnsUsed}</div>
                        )}
                        {submissionDetails.SubmissionData?.totalTokensUsed && (
                          <div className="kpi">Tokens: {submissionDetails.SubmissionData.totalTokensUsed.toLocaleString()}</div>
                        )}
                        {/* Fallback to SubmissionContextData stats if SubmissionData doesn't have them */}
                        {!submissionDetails.SubmissionData?.turnsUsed && submissionDetails.SubmissionContextData?.totalTurns && (
                          <div className="kpi">Turns: {submissionDetails.SubmissionContextData.totalTurns}</div>
                        )}
                        {!submissionDetails.SubmissionData?.totalTokensUsed && submissionDetails.SubmissionContextData?.totalTokensUsed && (
                          <div className="kpi">Tokens: {submissionDetails.SubmissionContextData.totalTokensUsed.toLocaleString()}</div>
                        )}
                      </div>
                    </summary>
                    <div className="detailsBody">
                      {/* Context data */}
                      {submissionDetails.SubmissionContextData && (
                        viewMode === 'json' ? (
                          <pre className="content-box json-view">
                            {JSON.stringify(submissionDetails.SubmissionContextData, null, 2)}
                          </pre>
                        ) : (
                          <div className="content-box formatted-view">
                            {renderFormattedData(submissionDetails.SubmissionContextData)}
                          </div>
                        )
                      )}
                    </div>
                  </details>
                )}

                {/* Collapsible details: Rubric */}
                {rubricCriteria && (
                  <details>
                    <summary>
                      <div className="sumLeft">
                        <div className="caret" aria-hidden="true" />
                        <div>
                          <div className="sumTitle">Rubric: {rubricCriteria.Name}</div>
                          <div className="sumMeta">Criteria and scoring levels</div>
                        </div>
                      </div>
                      <div className="sumMeta">Max: {rubricCriteria.MaxScore}</div>
                    </summary>
                    <div className="detailsBody">
                      <div className="rubric-panel">
                        <div className="rubric-info">
                          <span className="rubric-code">{rubricCriteria.Code}</span>
                          <span className="rubric-maxscore">Max Score: {rubricCriteria.MaxScore}</span>
                        </div>
                        <div className="rubric-levels">
                          {rubricCriteria.Criterion.levels.map((level, idx) => (
                            <div key={idx} className="rubric-level">
                              <div className="level-header">
                                <span className="level-name">{level.level}</span>
                                <span className="level-score">{level.score} points</span>
                              </div>
                              <p className="level-description">{level.description}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </details>
                )}
              </div>
            </>
          ) : (
            <div className="empty-state">
              <p>Select a submission from the rail to start grading</p>
            </div>
          )}
        </main>

        {/* RIGHT DRAWER */}
        <aside className="drawer" aria-label="Grading drawer">
          <div className="drawerHead">
            <h3>Grade</h3>
            <label className="drawerToggleLabel" htmlFor="drawerToggle" title="Collapse/Expand">⟷</label>
          </div>

          <div className="drawerBody">
            {/* Rubric panel (collapsible) */}
            {rubricCriteria && (
              <details className="card rubric-card" open>
                <summary className="rubric-summary">
                  <span className="fieldLabel">Rubric: {rubricCriteria.Name}</span>
                  <span className="rubric-max">Max: {rubricCriteria.MaxScore}</span>
                </summary>
                <div className="rubric-levels-compact">
                  {rubricCriteria.Criterion.levels.map((level, idx) => (
                    <div key={idx} className="rubric-level-compact">
                      <div className="level-header-compact">
                        <span className="level-name">{level.level}</span>
                        <span className="level-score">{level.score}pts</span>
                      </div>
                      <p className="level-description-compact">{level.description}</p>
                    </div>
                  ))}
                </div>
              </details>
            )}

            {/* Score card */}
            <div className="card">
              <div className="fieldLabel">Score</div>
              <div className="scoreRow">
                <input
                  className="scoreInput"
                  type="text"
                  inputMode="decimal"
                  value={currentScore === 0 ? "" : currentScore}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "" || val === ".") {
                      setCurrentScore(0);
                    } else {
                      const parsed = parseFloat(val);
                      if (!isNaN(parsed) && parsed >= 0 && parsed <= maxScore) {
                        setCurrentScore(parsed);
                      }
                    }
                  }}
                  onBlur={(e) => {
                    // Ensure valid number on blur
                    const parsed = parseFloat(e.target.value);
                    if (isNaN(parsed) || parsed < 0) {
                      setCurrentScore(0);
                    }
                  }}
                  placeholder="0"
                />
                <div className="outOf">/ {maxScore}</div>
              </div>
              <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {currentGrade && <span className="badge">{currentGrade}</span>}
                {aiEvaluation && (
                  <span className="badge good">AI: {aiEvaluation.score}/{aiEvaluation.maxScore}</span>
                )}
              </div>

              {/* AI evaluation results inline */}
              {aiEvaluation && (
                <div className="ai-results-compact">
                  <div className="ai-score-compact">
                    <strong>AI Suggested:</strong> {aiEvaluation.score}/{aiEvaluation.maxScore} ({aiEvaluation.grade})
                  </div>
                  <div className="ai-feedback-compact">
                    <strong>AI Feedback:</strong>
                    <div className="markdown-content"><ReactMarkdown>{aiEvaluation.feedback}</ReactMarkdown></div>
                  </div>
                  {aiEvaluation.criteriaScores && aiEvaluation.criteriaScores.length > 0 && (
                    <div className="ai-criteria-compact">
                      <strong>Criteria Breakdown:</strong>
                      {aiEvaluation.criteriaScores.map((criterion, idx) => (
                        <div key={idx} className="criterion-item-compact">
                          <span className="criterion-name">{criterion.criterionName}:</span>
                          <span className="criterion-score">
                            {criterion.score}/{criterion.maxScore}
                          </span>
                          <div className="criterion-feedback"><ReactMarkdown>{criterion.feedback}</ReactMarkdown></div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Feedback card */}
            <div className="card">
              <div className="fieldLabel" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Overall Feedback</span>
                <button
                  type="button"
                  className="tool"
                  style={{ padding: '4px 8px', fontSize: '11px' }}
                  onClick={() => setFeedbackEditMode(!feedbackEditMode)}
                >
                  {feedbackEditMode ? 'View' : 'Edit'}
                </button>
              </div>
              {feedbackEditMode ? (
                <textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder="What the student did well, what to improve, and next steps..."
                />
              ) : feedbackText ? (
                <div className="markdown-content feedback-view">
                  <ReactMarkdown>{feedbackText}</ReactMarkdown>
                </div>
              ) : (
                <div style={{ color: 'var(--muted2)', padding: '10px', fontSize: '13px' }}>
                  No feedback yet. Click Edit to add feedback.
                </div>
              )}
            </div>

            {/* Reviewer notes card */}
            <div className="card">
              <div className="fieldLabel">Reviewer Notes (Internal)</div>
              <textarea
                value={reviewerComments}
                onChange={(e) => setReviewerComments(e.target.value)}
                placeholder="Private notes for moderation / internal tracking..."
                style={{ minHeight: 90 }}
              />
            </div>

            {/* Quick actions card */}
            {selectedSubmission && (
              <div className="card">
                <div className="fieldLabel">Quick Actions</div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button className="btn" type="button" onClick={() => handleResetGradeClick(selectedSubmission.id)}>
                    Reset Grade
                  </button>
                  <button className="btn" type="button" onClick={() => handleClearDataClick(selectedSubmission.id)}>
                    Clear Data
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="drawerFooter">
            <button className="btn" type="button" disabled={!selectedSubmission || saving}>
              Save Draft
            </button>
            <button
              className="btn primary"
              type="button"
              onClick={handleSaveAndNext}
              disabled={saving || !selectedSubmission || !currentScore}
            >
              {saving ? 'Saving...' : 'Submit Grade'}
            </button>
          </div>
        </aside>
      </div>

      {/* Reset Grade Modal */}
      {showResetModal && (
        <div className="modal-overlay" role="presentation" onClick={() => setShowResetModal(false)}>
          <div className="modal-content" role="alertdialog" aria-modal="true" aria-labelledby="reset-grade-title" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 id="reset-grade-title">Reset Grade</h3>
              <button className="modal-close" onClick={() => setShowResetModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="modal-warning">
                <span className="warning-icon">&#9888;&#65039;</span>
                <p>Are you sure you want to reset ALL grade information for submission #{modalSubmissionId}?</p>
              </div>
              <div className="modal-info">
                <p><strong>This will clear:</strong></p>
                <ul>
                  <li>Score, MaxScore, PercentageScore</li>
                  <li>Grade and IsPassed status</li>
                  <li>FeedbackText and ReviewerComments</li>
                  <li>GradedAt and ReturnedAt timestamps</li>
                  <li>ReviewedByUserId</li>
                </ul>
                <p><strong>Status will be reset to:</strong> SUBMITTED</p>
                <p className="modal-note">Submission data (student's work) will remain intact</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-modal-cancel" onClick={() => setShowResetModal(false)}>
                Cancel
              </button>
              <button className="btn-modal-confirm" onClick={confirmResetGrade}>
                Reset Grade
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Submission Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" role="presentation" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content modal-danger" role="alertdialog" aria-modal="true" aria-labelledby="delete-submission-title" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 id="delete-submission-title">Delete Submission</h3>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="modal-warning modal-danger-warning">
                <span className="warning-icon">&#128680;</span>
                <p>DANGER: You are about to permanently delete submission #{modalSubmissionId}</p>
              </div>
              <div className="modal-info modal-danger-info">
                <p><strong>This action will:</strong></p>
                <ul>
                  <li><strong>Permanently delete</strong> the entire submission record</li>
                  <li>Remove all submission data (student's work)</li>
                  <li>Remove all context data (prompts, outputs, stats)</li>
                  <li>Remove all grading information</li>
                  <li>Remove all metadata and timestamps</li>
                </ul>
                <p className="modal-danger-note">
                  <strong>THIS CANNOT BE UNDONE!</strong><br/>
                  The submission will be gone forever.
                </p>
              </div>
              <div className="modal-confirmation">
                <p>Are you absolutely sure you want to proceed?</p>
              </div>
              <div className="modal-options" style={{ marginTop: '16px' }}>
                <label className="modal-checkbox">
                  <input
                    type="checkbox"
                    checked={resetSessionOnDelete}
                    onChange={(e) => setResetSessionOnDelete(e.target.checked)}
                  />
                  <div className="checkbox-content">
                    <strong>Also reset workspace session</strong>
                    <span className="checkbox-description">Allows student to start a new conversation (recommended)</span>
                  </div>
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-modal-cancel" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button className="btn-modal-danger" onClick={confirmDeleteSubmission}>
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Session Modal */}
      {showResetSessionModal && resetSessionSubmission && (
        <div className="modal-overlay" role="presentation" onClick={() => setShowResetSessionModal(false)}>
          <div className="modal-content" role="alertdialog" aria-modal="true" aria-labelledby="reset-session-title" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 id="reset-session-title">Reset Workspace Session</h3>
              <button className="modal-close" onClick={() => setShowResetSessionModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="modal-warning">
                <span className="warning-icon">&#128260;</span>
                <p>Reset workspace session for <strong>{resetSessionSubmission.userName || `User #${resetSessionSubmission.userId}`}</strong></p>
              </div>
              <div className="modal-info">
                <p><strong>This will:</strong></p>
                <ul>
                  <li>Delete the student's AI workspace session</li>
                  <li>Allow them to start a new conversation from scratch</li>
                  <li>Clear their conversation history with the AI tutor</li>
                </ul>
                <p style={{ marginTop: '12px', color: 'var(--muted2)' }}>
                  <em>Note: This does NOT delete the submission record. Use this when a student needs to restart their workspace without losing their graded submission.</em>
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-modal-cancel" onClick={() => setShowResetSessionModal(false)}>
                Cancel
              </button>
              <button className="btn-modal-confirm" onClick={confirmResetSession}>
                Reset Session
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clear Data Modal */}
      {showClearModal && (
        <div className="modal-overlay" role="presentation" onClick={() => setShowClearModal(false)}>
          <div className="modal-content" role="alertdialog" aria-modal="true" aria-labelledby="clear-data-title" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 id="clear-data-title">Clear Submission Data</h3>
              <button className="modal-close" onClick={() => setShowClearModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="modal-warning">
                <span className="warning-icon">&#9888;&#65039;</span>
                <p>Select what data to clear for submission #{modalSubmissionId}</p>
              </div>
              <div className="modal-options">
                <label className="modal-checkbox">
                  <input
                    type="checkbox"
                    checked={clearOptions.clearSubmissionData}
                    onChange={(e) => setClearOptions({...clearOptions, clearSubmissionData: e.target.checked})}
                  />
                  <div className="checkbox-content">
                    <strong>Clear Submission Data (Gradable Content)</strong>
                    <span className="checkbox-description">Clears reflections, answers, checklist - the content that gets graded</span>
                  </div>
                </label>
                <label className="modal-checkbox">
                  <input
                    type="checkbox"
                    checked={clearOptions.clearSubmissionContextData}
                    onChange={(e) => setClearOptions({...clearOptions, clearSubmissionContextData: e.target.checked})}
                  />
                  <div className="checkbox-content">
                    <strong>Clear Submission Context Data</strong>
                    <span className="checkbox-description">Clears prompts, AI outputs, session stats - supporting context</span>
                  </div>
                </label>
                <label className="modal-checkbox">
                  <input
                    type="checkbox"
                    checked={clearOptions.resetStatus}
                    onChange={(e) => setClearOptions({...clearOptions, resetStatus: e.target.checked})}
                  />
                  <div className="checkbox-content">
                    <strong>Reset Status to SUBMITTED</strong>
                    <span className="checkbox-description">Clears all grading info (same as Reset Grade)</span>
                  </div>
                </label>
              </div>
              {!clearOptions.clearSubmissionData && !clearOptions.clearSubmissionContextData && !clearOptions.resetStatus && (
                <p className="modal-error">Please select at least one option</p>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-modal-cancel" onClick={() => setShowClearModal(false)}>
                Cancel
              </button>
              <button
                className="btn-modal-confirm"
                onClick={confirmClearData}
                disabled={!clearOptions.clearSubmissionData && !clearOptions.clearSubmissionContextData && !clearOptions.resetStatus}
              >
                Clear Data
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIGradingStudio;
